import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { PRODUCTS, CATEGORIES } from "@/data/anabiya";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useEffectiveProducts, useEffectiveCategories, resolveImage } from "@/context/PricesContext";
import { SHOP } from "@/constants/testIds";
import { Navbar } from "@/components/Navbar";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { FloatingButtons } from "@/components/FloatingButtons";
import { Seo } from "@/components/Seo";
import { deriveCategorySeo, productAltText } from "@/lib/seoDerive";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";

const SORTS = [
  { slug: "featured", label: "Featured" },
  { slug: "price-asc", label: "Price · Low to High" },
  { slug: "price-desc", label: "Price · High to Low" },
  { slug: "rating", label: "Top Rated" },
];

export const ShopPage = () => {
  useSmoothScroll();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const initialCat = params.get("category") || "all";
  const [active, setActive] = useState(initialCat);
  const [sort, setSort] = useState("featured");
  const { format } = useCurrency();
  const { addItem } = useCart();
  const all = useEffectiveProducts(PRODUCTS);
  const cats = useEffectiveCategories(CATEGORIES);
  const filterTabs = useMemo(() => [{ slug: "all", label: "All" }, ...cats], [cats]);

  useEffect(() => {
    const cat = params.get("category") || "all";
    setActive(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const syncUrl = (next) => {
    const p = new URLSearchParams(location.search);
    if (next.category !== undefined) {
      if (next.category === "all") p.delete("category"); else p.set("category", next.category);
    }
    navigate({ pathname: "/shop", search: p.toString() ? `?${p.toString()}` : "" }, { replace: true });
  };

  const setCategory = (slug) => {
    setActive(slug);
    syncUrl({ category: slug });
  };

  const list = useMemo(() => {
    let filtered = active === "all" ? [...all] : all.filter((p) => p.category === active);
    if (sort === "price-asc") filtered.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") filtered.sort((a, b) => b.price - a.price);
    else if (sort === "rating") filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    else filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return filtered;
  }, [active, sort, all]);

  const activeLabel = filterTabs.find((f) => f.slug === active)?.label || "All";

  // Category SEO — derived client-side from the SAME effective product data
  // the page renders (admin custom products + overrides included). No fetch,
  // no invented claims.
  const catSeo = useMemo(() => {
    if (active === "all") return null;
    const inCat = all.filter((p) => p.category === active);
    return deriveCategorySeo(active, activeLabel, inCat);
  }, [active, activeLabel, all]);

  return (
    <main data-testid={SHOP.root} className="relative bg-white min-h-screen">
      <Seo
        path={active === "all" ? "/shop" : `/shop?category=${active}`}
        title={active === "all"
          ? "The Anabiya Collection — Luxury Bedding, Home Textiles & Living"
          : (catSeo?.seo_title || `Luxury ${(cats.find((c) => c.slug === active)?.label) || activeLabel}`)}
        description={active === "all"
          ? "Explore The Anabiya Collection — luxury duvet covers, sheet sets, quilts, cushions and curtains. Premium home textiles crafted in Mumbai, shipped worldwide."
          : (catSeo?.meta_description || `Shop luxury ${(cats.find((c) => c.slug === active)?.label || activeLabel).toLowerCase()} by Anabiya Decor — premium fabrics, timeless design, international shipping.`)}
      />
      <AnnouncementBar />
      <Navbar />

      {/* header */}
      <section className="relative bg-bone border-b border-hairline pt-16 md:pt-24 pb-14 md:pb-20">
        <div className="px-6 md:px-12 lg:px-16">
          <div className="overline text-gold mb-5 flex items-center gap-3">
            <Link to="/" className="edit-link text-forest/70">Home</Link>
            <span aria-hidden>/</span>
            <span className="text-forest">Shop</span>
            {active !== "all" && (
              <>
                <span aria-hidden>/</span>
                <span className="text-forest">{activeLabel}</span>
              </>
            )}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
            aria-label={active === "all"
              ? "The Anabiya Collection — Luxury Bedding & Home Textiles"
              : `Luxury ${activeLabel} — Anabiya Decor`}
            className="font-display text-forest text-5xl md:text-7xl lg:text-[7rem] leading-[0.9] tracking-tight"
          >
            <span className="sr-only">
              {active === "all"
                ? "The Anabiya Collection — Luxury Bedding & Home Textiles"
                : `Luxury ${activeLabel} — Anabiya Decor`}
            </span>
            <span aria-hidden="true">
              {active === "all" ? (
                <>
                  The Anabiya <span className="font-editorial italic text-champagne">Collection.</span>
                </>
              ) : (
                <>
                  Luxury{" "}
                  {activeLabel.split(" ").map((w, i, a) => (
                    <span key={i}>
                      {i === a.length - 1 ? <span className="font-editorial italic text-champagne">{w}.</span> : `${w} `}
                    </span>
                  ))}
                </>
              )}
            </span>
          </motion.h1>
          <p className="mt-6 text-forest/60 text-base md:text-lg max-w-2xl font-light" data-testid="shop-category-intro">
            {active === "all"
              ? "Luxury bedding, home textiles & thoughtful pieces for beautiful living."
              : (catSeo?.intro || `Premium ${activeLabel.toLowerCase()} — crafted with timeless elegance, shipped worldwide from Mumbai.`)}
          </p>
        </div>
      </section>

      {/* filters + sort */}
      <section className="sticky top-16 md:top-20 z-30 bg-white/85 backdrop-blur-xl border-b border-hairline">
        <div className="px-6 md:px-12 lg:px-16 py-4 flex items-center justify-between gap-4 overflow-hidden">
          <div className="flex items-center gap-2 md:gap-3 overflow-x-auto no-scrollbar flex-1 min-w-0">
            {filterTabs.map((c) => {
              const on = active === c.slug;
              return (
                <button
                  key={c.slug}
                  data-testid={SHOP.filter(c.slug)}
                  onClick={() => setCategory(c.slug)}
                  className={`overline whitespace-nowrap rounded-full px-4 py-2 border transition-colors duration-500 ${
                    on ? "bg-forest text-white border-forest" : "text-forest border-hairline hover:border-forest/50"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div className="shrink-0">
            <select
              data-testid={SHOP.sort}
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-transparent border border-hairline rounded-full px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest transition-colors duration-500"
              aria-label="Sort by"
            >
              {SORTS.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>
      <section className="px-6 md:px-12 lg:px-16 py-14 md:py-20">
        {list.length === 0 ? (
          <div className="py-24 text-center">
            <p className="font-display text-forest text-3xl">
              No products in this category <span className="font-editorial italic text-champagne">just yet.</span>
            </p>
            <button
              onClick={() => setCategory("all")}
              className="mt-6 overline text-forest border border-forest/70 rounded-full px-5 py-3 hover:bg-forest hover:text-white transition-colors duration-500"
            >
              See everything →
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            {list.map((p, i) => {
              const soldOut = typeof p.stock === "number" && p.stock <= 0;
              return (
              <motion.article
                key={p.slug}
                data-testid={SHOP.card(p.slug)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (i % 8) * 0.03 }}
                className="group col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-3"
              >
                <Link to={`/product/${p.slug}`} className="block">
                  <div className="relative aspect-[4/5] overflow-hidden clip-slab bg-beige">
                    <img
                      src={resolveImage(p.images[0])}
                      alt={productAltText(p)}
                      loading="lazy"
                      className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.7,0,0.2,1)] group-hover:scale-[1.06] ${soldOut ? "opacity-60" : ""}`}
                    />
                    {soldOut ? (
                      <span data-testid={`sold-out-${p.slug}`} className="absolute top-3 left-3 overline bg-forest text-white px-2 py-1 rounded-full">Sold out</span>
                    ) : (
                      p.tag && (
                        <span className="absolute top-3 left-3 overline bg-gold text-white px-2 py-1 rounded-full">
                          {p.tag}
                        </span>
                      )
                    )}
                    {p.mrp && p.mrp > p.price && !soldOut && (
                      <span className="absolute bottom-3 left-3 overline bg-white/90 text-forest px-2 py-1 rounded-full">
                        Save {format(p.mrp - p.price)}
                      </span>
                    )}
                  </div>
                </Link>
                <div className="mt-4">
                  <Link
                    to={`/product/${p.slug}`}
                    className="font-display text-forest text-xl md:text-2xl leading-tight hover:text-gold transition-colors duration-500"
                  >
                    {p.title}
                  </Link>
                  <p className="mt-1 text-forest/60 text-sm">{p.caption}</p>
                  <div className="mt-3 flex items-baseline gap-3">
                    <span className="font-display text-forest text-xl tabnum">{format(p.price)}</span>
                    {p.mrp && p.mrp > p.price && (
                      <span className="text-forest/40 line-through text-sm tabnum">
                        {format(p.mrp)}
                      </span>
                    )}
                  </div>
                  <button
                    disabled={soldOut}
                    onClick={() =>
                      addItem({
                        slug: p.slug,
                        title: p.title,
                        price: p.price,
                        image: resolveImage(p.images[0]),
                        size: p.sizes?.[0] || "",
                        colour: p.colours?.[0]?.name || "",
                        qty: 1,
                      })
                    }
                    data-testid={`shop-add-${p.slug}`}
                    className="mt-4 inline-flex items-center gap-2 overline text-forest border border-forest/70 rounded-full px-4 py-2 hover:bg-forest hover:text-white transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ShoppingBag size={13} strokeWidth={1.6} /> {soldOut ? "Sold out" : "Add to Cart"}
                  </button>
                </div>
              </motion.article>
              );
            })}
          </div>
        )}
      </section>

      <Footer />
      <FloatingButtons />
    </main>
  );
};
